import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import "../../css/ursb-submit.css"
import { DocConfirmationIcon, DownloadIcon, PDFIcon, WarningEditConfirmationIcon } from '../../../../../asset-controller-mod/asset-serv/icons/iconExporter';
import { Button, Confirmation  } from '@core/uilibrary';
import SelectFieldNoLabel from '../../../../../component-controller-mod/component-serv/input-fs-cp/select-field-op/ts/selectFieldNoLabel';
import { CareerSubmitStatusAction, STATUS_OPTIONS, updateCareerApplicationStatusApi } from './submit.type';
import { useGlobalPopupProvider } from '../../../../../context-controller-mod/context-serv/popup-provider/ts/globalPopupProvider';
import { useToast } from '../../../../../context-controller-mod/context-serv/toast-provider/bottomToast/ts/ToastProvider';
import { groupPermissionAccessCMSCareer } from '../../../../../context-controller-mod/context-serv/permission-provider/permissionExporter';


type InboxFile = {
    name: string;
    sizeLabel?: string;
    url?: string;
};

export interface UsrbInboxProps {
    applicationId: string;
    jobId: string;
    applyFor: string;
    experiences: string;
    applyDate: string;
    email: string;
    phone?: string;
    status?: string;
    message?: string;
    file?: InboxFile;
    onBack?: () => void;
    onSaved?: () => void;
}

const actionFromUiStatus = (v: string): CareerSubmitStatusAction | null => {
    switch (v) {
        case "review":
            return "review";
        case "shortlisted":
            return "shortlisted";
        case "rejected":
            return "rejected";
        case "hired":
            return "hired";
        default:
            return null;
    }
};


const UrsbSubmit: FC<UsrbInboxProps> = ({
    applicationId,
    jobId,
    applyFor,
    experiences,
    applyDate,
    email,
    message,
    phone,
    status,
    file,
    onBack,
    onSaved,
}) => {

    const { openConfirm, closeConfirm } = useGlobalPopupProvider();
    const { showToast } = useToast();

    const initialStatus = useMemo(() => {
        return status && STATUS_OPTIONS.some((o) => o.value === status) ? status : "new";
    }, [status]);

    const [statusFilter, setStatusFilter] = useState<string>(initialStatus);
    const [saving, setSaving] = useState(false);

    const canUpdate = groupPermissionAccessCMSCareer.update()


    useEffect(() => {
        setStatusFilter(initialStatus);
    }, [initialStatus]);

    const forceDownload = useCallback(async (url: string, filename: string) => {
        try {
            const res = await fetch(url, { credentials: "include" });
            if (!res.ok) throw new Error(`Download failed (${res.status})`);

            const blob = await res.blob();
            const blobUrl = globalThis.window.URL.createObjectURL(blob);

            const a = document.createElement("a");
            a.href = blobUrl;
            a.download = filename || "download";
            document.body.appendChild(a);
            a.click();
            a.remove();

            globalThis.window.URL.revokeObjectURL(blobUrl);
        } catch (err) {
            console.error(err);
        }
    }, []);

    const selectedAction = useMemo(
        () => actionFromUiStatus(statusFilter),
        [statusFilter]
    );

    const isNew = statusFilter === "new";
    const isInitialNew = initialStatus === "new";

    const canSave =
        !saving &&
        !isNew &&
        statusFilter !== initialStatus &&
        selectedAction !== null;

    const onSave = useCallback(() => {
        if (!selectedAction) return;

        openConfirm(
            <Confirmation
                visible
                title="Change status?"
                description="Are you sure you want to update this candidate status?"
                status="warning"
                onClose={closeConfirm}
                icon={<DocConfirmationIcon/>}
                buttons={
                    <>
                        <Button variant="ghost" value="Cancel" disabled={saving} onClick={closeConfirm} />
                        <Button
                            variant="primary"
                            value={saving ? "Saving..." : "Confirm"}
                            disabled={saving}
                            onClick={async () => {
                                try {
                                    setSaving(true);
                                    await updateCareerApplicationStatusApi(applicationId, selectedAction);
                                    showToast({ status: "success", title: "Updated", description: "Status updated successfully." });
                                    closeConfirm();
                                    onSaved?.();
                                } catch (e) {
                                    showToast({ status: "error", title: "Failed", description: "Status update failed." });
                                } finally {
                                    setSaving(false);
                                }
                            }}
                        />
                    </>
                }
            />
        );
    }, [applicationId, selectedAction, openConfirm, closeConfirm, saving, onSaved, showToast]);


    return (
        <section className="scl--ursb-crr-smb-inbox-const-styl-box">
            <div className="scl--ursb-crr-smb-item-content-styl ocasion">
                <div className="scl--ursb-crr-smb-spec-sm-item">
                    <span>Job ID:</span>
                    <h6>{jobId}</h6>
                </div>

                <div className="scl--ursb-crr-smb-spec-sm-item">
                    <span>Apply for:</span>
                    <h6>{applyFor}</h6>
                </div>
            </div>

            <div className="scl--ursb-crr-smb-item-content-styl ocasion">
                <div className="scl--ursb-crr-smb-spec-sm-item">
                    <span>Experience :</span>
                    <h6>{experiences}</h6>
                </div>

                <div className="scl--ursb-crr-smb-spec-sm-item">
                    <span>Apply date:</span>
                    <h6>{applyDate}</h6>
                </div>
            </div>

            <div className="scl--ursb-crr-smb-item-content-styl normal">
                <span>Email</span>
                <h6>{email}</h6>
            </div>

            <div className="scl--ursb-crr-smb-item-content-styl normal">
                <span>Phone</span>
                <h6>{phone}</h6>
            </div>

            {canUpdate &&
                <div className="scl--ursb-crr-smb-item-content-styl normal">
                    <span>Status</span>
                    <SelectFieldNoLabel
                        name="status"
                        placeholder="Status"
                        value={statusFilter}
                        options={STATUS_OPTIONS}
                        onChange={(val) => setStatusFilter(String(val))}
                    />
                </div>
            }

            <div className="scl--ursb-crr-smb-item-content-styl normal">
                <span>Attached file</span>

                <div className="scl--ursb-crr-smb-file-display-styl">
                    {file ? (
                        <>
                            <PDFIcon />
                            <div className="scl--ursb-crr-smb-file-ds-title">
                                <h5>{file.name}</h5>
                                <div className="span-mock">{file.sizeLabel ?? "-"}</div>
                            </div>

                            <button
                                type="button"
                                onClick={() => {
                                    if (file?.url) forceDownload(file.url, file.name);
                                }}
                                disabled={!file?.url}
                            >
                                <DownloadIcon />
                            </button>
                        </>
                    ) : (
                        <div className='no-file'>
                            No file were attached
                        </div>
                    )}
                </div>
            </div>

            <div className="scl--ursb-crr-smb-item-content-styl normal">
                <span>Message</span>
                <div className="message-box">{message}</div>
            </div>

            <div className="scl--ursb-crr-smb-btn-decision-styl">
                <Button value="Back" variant="ghost" onClick={onBack} />

                {canUpdate &&
                    <Button
                        value={saving ? "Saving..." : "Save"}
                        state={canSave ? "default" : "disabled"}
                        onClick={() => {
                            if (!canSave) return;
                            onSave();
                        }}
                    />
                }

            </div>
        </section>
    );
};

export default UrsbSubmit